/** @jsxImportSource @emotion/react */

import React from "react";
import Immutable from "immutable";
import {css} from "@emotion/react";
import {Chip, TextField} from "@mui/material";

import Dialog from "js/common/views/tabs-dialog";
import ConfirmDialog from "js/onpoint/dialog";
import * as Users from "js/common/users";
import * as Groups from "js/common/groups";
import * as ScheduledReports from "js/common/scheduled-reports";
import * as Popups from "js/common/popups";
import DelayedScheduleNameInput from "js/dashboards/scheduling/delayed-schedule-name-input";
import DelayedTextField from "js/common/views/inputs/simple-text-input";
import Select from "js/common/views/inputs/immutable-react-select";
import RruleScheduler from "js/dashboards/scheduling/rrule-scheduler";
import UserAndGroupPicker from "js/common/views/inputs/group-and-user-picker/dropdown-user-group-picker";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {TextButton} from "js/common/views/inputs/buttons";
import ScheduleUnsavedChangesDialog from "js/dashboards/scheduling/schedule-unsaved-changes-dialog";
import ScheduleAccessDialog from "js/dashboards/scheduling/schedule-access-dialog";
import Tooltip from "js/common/views/tooltips";
import throttle from "js/common/utils/throttle";
import * as Auditor from "js/common/auditor";
import {getDefaultSchedule} from "js/dashboards/scheduling/utils";
import {getUniqueName} from "js/common/utils/unique-naming";
import FormField from "js/common/views/inputs/form-field";
import Pluralize from "pluralize";
import {apps, currentUserCanAccessApp} from "js/common/permissions";

const isRequiredMessage = "This field is required";
const missingRecipientsMessage = "At least one recipient is required";
const PREVIEW_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

const ScheduleFormContent = React.memo(
    ({
      configId,
      idToDashboard,
      SavedConfigs,
      scheduleToEdit,
      cloneFromId,
      idToScheduledReport,
      onScheduleUpdate,
      onScheduleDelete,
      onCloneClick,
      existingSchedules,
      addNewMode,
      onRequestClose,
      isNewReport,
      isClonedFromEdit = false
    }) => {
      const cloneFromIdObject = cloneFromId ? idToScheduledReport?.get(cloneFromId) : null;
      const {theme} = React.useContext(CustomThemeContext);

      const [emailInput, setEmailInput] = React.useState("");
      const [errors, setErrors] = React.useState(Immutable.Map());
      const [emailError, setEmailError] = React.useState("");
      const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
      const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
      const [showAccessDialog, setShowAccessDialog] = React.useState(false);
      const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
      const [dashboardShareData, setDashboardShareData] = React.useState(null);
      const [hasInteractedWithRecipients, setHasInteractedWithRecipients] = React.useState(false);
      const justClonedRef = React.useRef(false);

      const [scheduleConfig, setScheduleConfig] = React.useState(() => {
        if (scheduleToEdit) {
          return Immutable.Map({
            id: scheduleToEdit.get("id"),
            name: scheduleToEdit.get("name"),
            dashboardId: scheduleToEdit.get("dashboardId"),
            schedule: Immutable.Map({
              rrule: scheduleToEdit.get("recurrenceSchedule"),
              startDate: scheduleToEdit.get("localStartDate"),
              startTime: scheduleToEdit.get("localStartTime"),
              timezone: scheduleToEdit.get("timezone")
            }),
            internalRecipientUserIds: scheduleToEdit.get("destUserIds", Immutable.List()),
            internalRecipientGroupIds: scheduleToEdit.get("destGroupIds", Immutable.List()),
            externalRecipients: scheduleToEdit.get("destEmailAddresses", Immutable.List()),
            emailSubject: scheduleToEdit.get("emailSubject"),
            emailBody: scheduleToEdit.get("emailBody"),
            ownerId: scheduleToEdit.get("ownerId")
          });
        }


        const dashboardName = idToDashboard.getIn([configId, "name"]);
        return Immutable.Map({
          name: `${dashboardName} Report`,
          dashboardId: configId,
          schedule: getDefaultSchedule(),
          internalRecipientUserIds: Immutable.List(),
          internalRecipientGroupIds: Immutable.List(),
          externalRecipients: Immutable.List(),
          emailSubject: null,
          emailBody: null,
          ownerId: Users.getCurrentUser().get("id")
        });
      });

      const [originalConfig, setOriginalConfig] = React.useState(scheduleConfig);
      const [accessData, setAccessData] = React.useState(
          Immutable.Map({
            usersWithoutAccess: Immutable.List(),
            groupsWithoutAccess: Immutable.List(),
            usersWithAccess: Immutable.List(),
            groupsWithAccess: Immutable.List()
          })
      );
      const [isCheckingAccess, setIsCheckingAccess] = React.useState(false);
      const [pendingPreviews, setPendingPreviews] = React.useState(Immutable.List());
      const [previewError, setPreviewError] = React.useState(null);
      const [isWaitingForPreview, setIsWaitingForPreview] = React.useState(false);
      const sentPreviewIdsRef = React.useRef(Immutable.Set());
      const previewSentAtRef = React.useRef(null);

      React.useEffect(() => {
        if (cloneFromIdObject && !scheduleToEdit) {
          const allExistingNames = existingSchedules.map((schedule) => schedule.get("name")).toArray();
          const clonedName = getUniqueName(cloneFromIdObject.get("name"), allExistingNames);

          const scheduleObj = Immutable.Map({
            rrule: cloneFromIdObject.get("recurrenceSchedule"),
            startDate: cloneFromIdObject.get("localStartDate"),
            startTime: cloneFromIdObject.get("localStartTime"),
            timezone: cloneFromIdObject.get("timezone")
          });

          const newConfig = Immutable.Map({
            name: clonedName,
            dashboardId: cloneFromIdObject.get("dashboardId"),
            schedule: scheduleObj,
            internalRecipientUserIds: cloneFromIdObject.get("destUserIds", Immutable.List()),
            internalRecipientGroupIds: cloneFromIdObject.get("destGroupIds", Immutable.List()),
            externalRecipients: cloneFromIdObject.get("destEmailAddresses", Immutable.List()),
            emailSubject: cloneFromIdObject.get("emailSubject"),
            emailBody: cloneFromIdObject.get("emailBody"),
            ownerId: Users.getCurrentUser().get("id")
          });


          justClonedRef.current = true;
          setScheduleConfig(newConfig);
          setOriginalConfig(newConfig);
          setHasUnsavedChanges(false);
          setEmailInput("");
        }
      }, [cloneFromIdObject, scheduleToEdit, existingSchedules]);

      const handleDeleteButtonClick = () => {
        setIsDeleteDialogOpen(true);
      };

      const handleConfirmDelete = () => {
        const scheduleId = scheduleConfig.get("id");
        if (scheduleId) {
          ScheduledReports.deleteScheduledReport(scheduleId, scheduleToEdit).then(() => {
            onScheduleDelete?.(scheduleId, scheduleToEdit.get("dashboardId"));
            setIsDeleteDialogOpen(false);
            onRequestClose();
            Auditor.audit("schedule-report:deleted", {
              dashboardId: scheduleToEdit.get("dashboardId"),
              name: scheduleToEdit.get("name")
            });
            Popups.success(
                "Successfully deleted scheduled report!",
                "top-right"
            );
          }).catch((error) => {
            console.error("Failed to delete scheduled report:", error);
            Popups.error(
                "Failed to delete scheduled report!",
                "top-right"
            );
          });
        }
      };

      const handleCloneButtonClick = () => {
        const clonedConfig = scheduleConfig.delete("id");

        const allExistingNames = existingSchedules.map((schedule) => schedule.get("name")).toArray();

        const clonedName = getUniqueName(clonedConfig.get("name"), allExistingNames);

        const finalClonedConfig = clonedConfig.set("name", clonedName);

        justClonedRef.current = true;
        setScheduleConfig(finalClonedConfig);

        setHasUnsavedChanges(false);
        setEmailInput("");

        Auditor.audit("schedule-report:cloned", {
          dashboardId: finalClonedConfig.get("dashboardId"),
          originalName: scheduleConfig.get("name"),
          clonedName: clonedName
        });

        if (onCloneClick) {
          onCloneClick(finalClonedConfig);
        }
      };

      const DRAWER_WIDTH = "80%";
      const scheduleContainerRef = React.useRef(null);

      const isEditMode = !!scheduleToEdit && !!scheduleConfig.get("id");
      const dashboardId = scheduleConfig.get("dashboardId") || configId;
      const dashboardName = idToDashboard.getIn([dashboardId, "name"]);

      const emailPlaceholders = React.useMemo(() => {
        const name = scheduleConfig.get("name") || "{Report Name}";
        const dateRange = "{Report Source Date Range}";
        return Immutable.Map({
          emailSubject: `${name} - ${dateRange}`,
          emailBody: `Hi,\n\nPlease find your ${name} report attached. This report contains the latest data and insights ${dateRange}.`
        });
      }, [scheduleConfig]);

      React.useEffect(() => {
        if (justClonedRef.current) {
          justClonedRef.current = false;
          return;
        }
        if (!isEditMode && dashboardName) {
          setScheduleConfig((prev) => prev.set("name", `${dashboardName} Report`));
        }
      }, [dashboardName, isEditMode]);

      React.useEffect(() => {
        if (dashboardId) {
          SavedConfigs.getShareList(dashboardId).then(
              (shareData) => setDashboardShareData(shareData),
              () => setDashboardShareData(Immutable.Map())
          );
        }
      }, [SavedConfigs, dashboardId]);

      React.useEffect(() => {
        const isModified = !Immutable.is(scheduleConfig, originalConfig);
        setHasUnsavedChanges(isModified);
      }, [scheduleConfig, originalConfig]);

      // eslint-disable-next-line react-hooks/exhaustive-deps
      const handleSendPreview = React.useCallback(
          throttle(() => {
            Auditor.audit("schedule-report:preview", {
              dashboardId: scheduleConfig.get("dashboardId")
            });
            const previewConfig = Immutable.Map({
              "dashboardId": scheduleConfig.get("dashboardId"),
              "ownerId": scheduleConfig.get("ownerId"),
              "userId": Users.getCurrentUser().get("id"),
              "name": scheduleConfig.get("name"),
              "emailSubject": scheduleConfig.get("emailSubject"),
              "emailBody": scheduleConfig.get("emailBody")
            });
            ScheduledReports.requestPreview(previewConfig)
                .then(newPreview => {
                  sentPreviewIdsRef.current = sentPreviewIdsRef.current.add(newPreview.get("id"));
                  previewSentAtRef.current = Date.now();
                  setPendingPreviews(oldPreviews => oldPreviews.push(newPreview));
                  Popups.success("Report preview is sending - check your inbox shortly.", "top-right");
                  Auditor.audit("schedule-report:preview:success", {dashboardId: newPreview.get("dashboardId")});
                })
                .catch(() => {
                  Popups.error("Failed to request preview", "top-right");
                });
          }, 1000),
          [scheduleConfig]
      );

      React.useEffect(() => {
        if (!isWaitingForPreview) return;

        const pollPendingPreviews = () => {
          ScheduledReports.getPendingPreviewsForCurrentUser().then((previews) => {
            // Check if any of our sent previews are no longer in the pending list
            // (meaning they finished with COMPLETED or FAILED status)
            const pendingIds = Immutable.Set(previews.map((p) => p.get("id")));
            const finishedIds = sentPreviewIdsRef.current.subtract(pendingIds);

            if (!finishedIds.isEmpty()) {
              sentPreviewIdsRef.current = sentPreviewIdsRef.current.subtract(finishedIds);
              finishedIds.forEach((previewId) => {
                Auditor.audit("schedule-report:preview:finished", {previewId});
              });
            }

            // If our sent previews are still pending after the timeout, warn the user
            if (
                previewSentAtRef.current &&
                !sentPreviewIdsRef.current.isEmpty() &&
                Date.now() - previewSentAtRef.current > PREVIEW_TIMEOUT_MS
            ) {
              setPreviewError("There was an issue generating your report. Please try again or contact support.");
              sentPreviewIdsRef.current = Immutable.Set();
              previewSentAtRef.current = null;
              Auditor.audit("schedule-report:preview:timeout");
            }

            setPendingPreviews(previews);
            if (previews.isEmpty() && sentPreviewIdsRef.current.isEmpty()) {
              setIsWaitingForPreview(false);
            }
          });
        };

        pollPendingPreviews();
        const intervalId = window.setInterval(pollPendingPreviews, 30000);
        return () => {
          window.clearInterval(intervalId);
        };
      }, [isWaitingForPreview]);

      const internalRecipientUserIds = scheduleConfig.get("internalRecipientUserIds");
      const internalRecipientGroupIds = scheduleConfig.get("internalRecipientGroupIds");

      const recipientAccessData = React.useMemo(() => {
        const usersWithoutAccess = [];
        const groupsWithoutAccess = [];
        const usersWithAccess = [];
        const groupsWithAccess = [];

        if (!dashboardShareData) {
          return Immutable.Map({
            usersWithoutAccess: Immutable.List(),
            groupsWithoutAccess: Immutable.List(),
            usersWithAccess: Immutable.List(),
            groupsWithAccess: Immutable.List()
          });
        }

        internalRecipientUserIds.forEach((userId) => {
          const user = Users.getUser(userId);
          if (user) {
            const ownerId = idToDashboard.getIn([configId, "ownerId"]);
            let hasAccess = !!dashboardShareData.getIn(["users", userId]) || userId === ownerId;

            if (!hasAccess) {
              const userGroupId = user.get("groupId");
              const groupHierarchy = Groups.getGroupBreadcrumbs(userGroupId)
                  .map((g) => g.id)
                  .reverse();
              hasAccess = groupHierarchy.some((groupId) => !!dashboardShareData.getIn(["groups", groupId]));
            }

            const userObj = Immutable.Map({
              type: "user",
              id: userId,
              name: user.get("fullName") || user.get("email"),
              email: user.get("email")
            });

            if (hasAccess) {
              usersWithAccess.push(userObj);
            } else {
              usersWithoutAccess.push(userObj);
            }
          }
        });

        internalRecipientGroupIds.forEach((groupId) => {
          let hasGroupAccess = !!dashboardShareData.getIn(["groups", groupId]);

          if (!hasGroupAccess) {
            const groupHierarchy = Groups.getGroupBreadcrumbs(groupId)
                .map((g) => g.id)
                .reverse();
            hasGroupAccess = groupHierarchy.some(
                (parentGroupId) => !!dashboardShareData.getIn(["groups", parentGroupId])
            );
          }

          const group = Groups.getGroup(groupId);
          if (group) {
            const groupObj = Immutable.Map({
              type: "group",
              id: groupId,
              name: group.get("name")
            });

            if (hasGroupAccess) {
              groupsWithAccess.push(groupObj);
            } else {
              groupsWithoutAccess.push(groupObj);
            }
          }
        });

        return Immutable.Map({
          usersWithoutAccess: Immutable.List(usersWithoutAccess),
          groupsWithoutAccess: Immutable.List(groupsWithoutAccess),
          usersWithAccess: Immutable.List(usersWithAccess),
          groupsWithAccess: Immutable.List(groupsWithAccess)
        });
      }, [dashboardShareData, internalRecipientUserIds, internalRecipientGroupIds, configId, idToDashboard]);

      const accessLookup = React.useMemo(() => {
        const userAccessMap = {};
        const groupAccessMap = {};

        recipientAccessData.get("usersWithAccess").forEach((user) => {
          userAccessMap[user.get("id")] = true;
        });
        recipientAccessData.get("groupsWithAccess").forEach((group) => {
          groupAccessMap[group.get("id")] = true;
        });

        return {userAccessMap, groupAccessMap};
      }, [recipientAccessData]);

      const handleSaveSchedule = () => {
        setIsCheckingAccess(true);
        setHasInteractedWithRecipients(true);
        try {
          setAccessData(recipientAccessData);

          const hasUsersWithoutAccess = !recipientAccessData.get("usersWithoutAccess").isEmpty();
          const hasGroupsWithoutAccess = !recipientAccessData.get("groupsWithoutAccess").isEmpty();
          const hasExternalRecipients = !scheduleConfig.get("externalRecipients").isEmpty();

          if (!hasUsersWithoutAccess && !hasGroupsWithoutAccess && !hasExternalRecipients) {
            saveSchedule(Immutable.Map());
          } else {
            setShowAccessDialog(true);
          }
        } catch (error) {
          console.error("Error checking user access:", error);
        } finally {
          setIsCheckingAccess(false);
        }
      };

      const saveSchedule = () => {
        setShowAccessDialog(false);

        const scheduledReportData = Immutable.Map({
          dashboardId: scheduleConfig.get("dashboardId"),
          name: scheduleConfig.get("name"),
          emailSubject: scheduleConfig.get("emailSubject"),
          emailBody: scheduleConfig.get("emailBody"),
          localStartDate: scheduleConfig.get("schedule").get("startDate"),
          localStartTime: scheduleConfig.get("schedule").get("startTime"),
          timezone: scheduleConfig.get("schedule").get("timezone"),
          recurrenceSchedule: scheduleConfig.get("schedule").get("rrule"),
          destUserIds: scheduleConfig.get("internalRecipientUserIds").toJS(),
          destGroupIds: scheduleConfig.get("internalRecipientGroupIds").toJS(),
          destEmailAddresses: scheduleConfig.get("externalRecipients").toJS(),
          ownerId: scheduleConfig.get("ownerId")
        });

        Auditor.audit(isEditMode ? "schedule-report:update" : "schedule-report:save", {
          dashboardId: scheduleConfig.get("dashboardId"),
          scheduleId: scheduleConfig.get("id")
        });

        const hasId = !!scheduleConfig.get("id");
        const savePromise = hasId
            ? ScheduledReports.updateScheduledReport(scheduleConfig.get("id"), scheduledReportData)
            : ScheduledReports.createScheduledReport(scheduledReportData);

        savePromise
            .then(scheduledReport => {
              Popups.success(
                  hasId ? "Scheduled report updated successfully!" : "Scheduled report saved successfully!",
                  "top-right"
              );
              setHasUnsavedChanges(false);
              setEmailInput("");
              onScheduleUpdate(scheduledReport, cloneFromId);
              Auditor.audit(hasId ? "schedule-report:update:success" : "schedule-report:save:success", {
                dashboardId: scheduledReport.get("dashboardId"),
                scheduleId: scheduledReport.get("id")
              });
              onRequestClose();
            })
            .catch((error) => {
              console.error("Error saving scheduled report:", error);
              Popups.error(
                  isEditMode ? "Failed to update scheduled report" : "Failed to save scheduled report",
                  "top-right"
              );
            });
      };

      const handleCloseRequest = () => {
        const shouldShowWarning = localStorage.getItem("showScheduleUnsavedWarning") !== "false";

        if (hasUnsavedChanges && shouldShowWarning) {
          setShowUnsavedDialog(true);
        } else {
          onRequestClose();
        }
      };

      const handleDiscardChanges = () => {
        setShowUnsavedDialog(false);
        onRequestClose();
      };


      const validateEmail = (email) => {
        // Standard email validation regex (RFC 5322 simplified)
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return emailRegex.test(email);
      };

      const handleAddEmail = () => {
        if (!emailInput.trim()) {
          return;
        }
        const email = emailInput.trim();

        if (!validateEmail(email)) {
          setEmailError("Please enter a valid email address");
          return;
        }

        setHasInteractedWithRecipients(true);
        setScheduleConfig((prev) => {
          const currentEmails = prev.get("externalRecipients");
          if (!currentEmails.includes(email)) {
            return prev.set("externalRecipients", currentEmails.push(email));
          }
          return prev;
        });

        setEmailInput("");
        setEmailError("");
      };

      const handleEmailKeyPress = (key) => {
        if (key === "Enter") {
          handleAddEmail();
        }
      };

      const handleRemoveExternalEmail = (emailToRemove) => {
        setHasInteractedWithRecipients(true);
        setScheduleConfig((prev) => {
          const currentEmails = prev.get("externalRecipients");
          return prev.set(
              "externalRecipients",
              currentEmails.filter((email) => email !== emailToRemove)
          );
        });
      };

      const handleUserSelect = React.useCallback((userId) => {
        setHasInteractedWithRecipients(true);
        setScheduleConfig((prev) => {
          const currentRecipients = prev.get("internalRecipientUserIds");
          if (!currentRecipients.includes(userId)) {
            return prev.set("internalRecipientUserIds", currentRecipients.push(userId));
          }
          return prev;
        });
      }, []);

      const handleGroupSelect = React.useCallback((groupId) => {
        setHasInteractedWithRecipients(true);
        setScheduleConfig((prev) => {
          const currentGroups = prev.get("internalRecipientGroupIds");
          if (!currentGroups.includes(groupId)) {
            return prev.set("internalRecipientGroupIds", currentGroups.push(groupId));
          }
          return prev;
        });
      }, []);

      const handleRemoveUser = (userId) => {
        setHasInteractedWithRecipients(true);
        setScheduleConfig((prev) => {
          const currentUsers = prev.get("internalRecipientUserIds");
          return prev.set(
              "internalRecipientUserIds",
              currentUsers.filter((id) => id !== userId)
          );
        });
      };

      const handleRemoveGroup = (groupId) => {
        setHasInteractedWithRecipients(true);
        setScheduleConfig((prev) => {
          const currentGroups = prev.get("internalRecipientGroupIds");
          return prev.set(
              "internalRecipientGroupIds",
              currentGroups.filter((id) => id !== groupId)
          );
        });
      };

      const dashboardOptions = React.useMemo(() => {
        if (!idToDashboard) {
          return Immutable.List();
        }

        return idToDashboard
            .map((dashboard, id) =>
                Immutable.Map({
                  value: id,
                  label: dashboard.get("name")
                })
            )
            .valueSeq()
            .sortBy((option) => option.get("label"))
            .toList();
      }, [idToDashboard]);

      const scheduleName = scheduleConfig.get("name");
      const externalRecipients = scheduleConfig.get("externalRecipients");

      const originalName = originalConfig.get("name");
      const originalDashboardId = originalConfig.get("dashboardId");
      const scheduleNamesToCheck = React.useMemo(() => {
        if (isEditMode) {
          const originalIndex = existingSchedules.findIndex(
              (schedule) => schedule.get("name") === originalName && schedule.get("dashboardId") === originalDashboardId
          );

          return existingSchedules.filter((_, index) => index !== originalIndex).map((report) => report.get("name"));
        }

        return existingSchedules.map((report) => report.get("name"));
      }, [isEditMode, existingSchedules, originalName, originalDashboardId]);

      React.useEffect(
          () => {
            const nameError =
                !scheduleName || scheduleName.length === 0 || scheduleName === ""
                    ? isRequiredMessage
                    : scheduleNamesToCheck.includes(scheduleName)
                        ? "Name must be unique"
                        : null;
            setErrors((prev) => prev.set("name", nameError));

            // Always validate recipients so Save button stays disabled when empty
            const hasRecipients =
                internalRecipientUserIds.count() > 0
                || internalRecipientGroupIds.count() > 0
                || externalRecipients.count() > 0;

            const recipientsError = hasRecipients ? null : missingRecipientsMessage;
            setErrors((prev) => prev.set("recipients", recipientsError));
          },
          [
            scheduleNamesToCheck,
            scheduleName,
            internalRecipientUserIds,
            internalRecipientGroupIds,
            externalRecipients,
            hasInteractedWithRecipients]);

      const isValidSchedule = React.useMemo(() => {
        return !errors.some((value) => value !== null);
      }, [errors]);

      const canSendPreview = React.useMemo(() => {
        return dashboardId != null;
      }, [dashboardId]);

      const sectionBarStyle = (theme) => ({
        display: "flex",
        alignItems: "center",
        background: theme.themeId === "light" ? "#f9f9f8" : theme.palette.background.card,
        padding: 12,
        fontWeight: 600,
        fontSize: 16
      });

      const fieldGridStyle = {
        display: "flex",
        flexDirection: "column",
        width: "49%",
        marginBottom: "1rem"
      };

      const chipContainerStyle = {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "12px",
        marginBottom: "16px"
      };

      const chipStyle = {
        borderRadius: 4,
        padding: "4px 6px",
        color: theme.palette.primary.main,
        background: theme.palette.background.light
      };

      const chipSx = {
        "& .MuiChip-deleteIcon": {
          color: theme.palette.text.main,
          opacity: 0.5,
          fontSize: 11,
          "&:hover": {
            color: theme.palette.text.main,
            opacity: 1,
            background: "transparent"
          }
        }
      };

      const strikethroughStyle = (theme) => css`
        width: 2px;
        height: 20px;
        rotate: 45deg;
        position: absolute;
        top: 0;
        left: 8px;
        background-color: ${theme.palette.text.lighter};
      `;

      const drawerControlsStyle = (theme, width) =>
          css`
            display: flex;
            padding: 1.25rem;
            border-top: 1px solid ${theme.palette.border.main};
            position: fixed;
            bottom: 0;
            right: 0;
            width: calc(${width} - 1px);
            z-index: 1;
            @media (max-width: 720px) {
              padding-left: 0;
              padding-right: 0;
              flex-direction: row;
            }
          `;

      const discardButtonStyle = () => css`
        background: #eee;
        margin: 0 10px;
        float: left;
        height: 42px;
        font-size: 15px;
      `;

      const drawerContentStyle = css`
        background: ${theme.palette.background.card};
        position: relative;
        padding: 0;
        margin-bottom: 74px;
        color: ${theme.palette.text.main};
        height: calc(100% - 112px);
        overflow-x: hidden;
        overflow-y: auto;
      `;

      const applyButtonStyle = css`
        margin: 0 10px;
        height: 42px;
        font-size: 15px;
      `;

      const userCanDelete = currentUserCanAccessApp(apps.scheduledReportAdmin) ||
          scheduleConfig.get("ownerId") === Users.getCurrentUser().get("id");

      return (
          <>
            <div css={drawerContentStyle} ref={scheduleContainerRef}>
              <div style={{padding: "16px 16px 0 16px"}}>
                <div style={{display: "flex", flexWrap: "wrap", justifyContent: "space-between"}}>
                  <div style={fieldGridStyle}>
                    <div>
                      <FormField
                          label={"Schedule Name"}
                          filled={!!scheduleConfig.get("name")}
                          errorMessage={errors.get("name")}
                      >
                        <DelayedScheduleNameInput
                            value={scheduleConfig.get("name")}
                            onChange={(name) => setScheduleConfig((prev) => prev.set("name", name))}
                            errors={errors}
                        />
                      </FormField>
                    </div>
                  </div>
                  <div style={fieldGridStyle}>
                    <div data-test-id={"schedule-report-select"}>
                      <FormField
                          label={"Report Source"}
                          required={true}
                          filled={!!dashboardId}
                          errorMessage={errors.get("dashboardId")}
                      >
                        <Select
                            selectedValue={dashboardId}
                            isMulti={false}
                            isClearable={false}
                            searchable={true}
                            placeholder="Select..."
                            isDisabled={!addNewMode}
                            style={{fontSize: 13}}
                            onChange={(newDashboardId) => {
                              setScheduleConfig((prev) => prev.set("dashboardId", newDashboardId));
                            }}
                            options={dashboardOptions}
                        />
                      </FormField>
                    </div>
                  </div>
                </div>
              </div>
              <div style={sectionBarStyle(theme)}>Scheduling</div>
              <div style={{padding: 16}}>
                <RruleScheduler
                    schedule={scheduleConfig.get("schedule")}
                    onChange={(schedule) => setScheduleConfig((prev) => prev.set("schedule", schedule))}
                    errors={errors}
                    onErrorsChange={(inputName, e) => setErrors((prev) => prev.set(inputName, e))}
                />
              </div>

              <div style={sectionBarStyle(theme)}>Delivery Settings</div>

              <div style={{padding: 16, display: "flex", justifyContent: "space-between"}}>
                <div style={{width: "49%"}}>
                  <div style={{marginBottom: 20}}>
                    <FormField
                        label={"Recipients"}
                        tooltip={
                          "Bullhorn Analytics users will receive a report PDF and a link to the report. External recipients will receive a report PDF only."
                        }
                        required={true}
                        filled={
                            !scheduleConfig.get("internalRecipientUserIds").isEmpty() ||
                            !scheduleConfig.get("internalRecipientGroupIds").isEmpty() ||
                            !scheduleConfig.get("externalRecipients").isEmpty()
                        }
                        errorMessage={hasInteractedWithRecipients ? errors.get("recipients") : null}
                    />
                  </div>
                  <FormField label={"Bullhorn Analytics Users and Groups"}>
                    <UserAndGroupPicker
                        showLoggedInUserOptions={false}
                        label="Add User or Group"
                        selectedUserIds={scheduleConfig.get("internalRecipientUserIds").toSet()}
                        selectedGroupIds={scheduleConfig.get("internalRecipientGroupIds").toSet()}
                        onGroupClick={handleGroupSelect}
                        onUserClick={handleUserSelect}
                        closeOnSelect={false}
                        maxContainerHeight={250}
                        customStyle={{height: 42}}
                        expandAll={true}
                        showInvisibleUsers={true}
                        identifyObservers={true}
                        includeCube19Users={false}
                        includeInactiveUsers={false}
                    />
                    {(!scheduleConfig.get("internalRecipientUserIds").isEmpty() ||
                        !scheduleConfig.get("internalRecipientGroupIds").isEmpty()) && (
                        <div style={chipContainerStyle}>
                          {scheduleConfig.get("internalRecipientUserIds").map((userId) => {
                            const user = Users.getUser(userId);
                            const hasAccess = accessLookup.userAccessMap[userId];

                            return user ? (
                                <Tooltip
                                    width={400}
                                    wrap={true}
                                    pointerStyle={{top: 20}}
                                    key={`user-${userId}`}
                                    text={
                                      hasAccess ? (
                                          ""
                                      ) : (
                                          <>
                                <span style={{fontSize: "10px", paddingLeft: 12, marginBottom: 0}}>
                                  No Report Source Access
                                </span>
                                            <p
                                                style={{
                                                  marginTop: 0,
                                                  fontSize: "13px",
                                                  padding: "6px 12px 6px 12px",
                                                  marginBottom: 0
                                                }}
                                            >
                                              This user does not have access to the report source and will receive a
                                              report PDF
                                              only. Manage their permissions to grant access to the report source.
                                            </p>
                                          </>
                                      )
                                    }
                                    disable={!!hasAccess}
                                    position="right"
                                >
                                  <Chip
                                      style={chipStyle}
                                      sx={chipSx}
                                      label={
                                        <span style={{display: "flex", alignItems: "center", gap: 4}}>
                                {!hasAccess && (
                                    <i
                                        className="bhi-caution"
                                        style={{
                                          fontSize: 12,
                                          marginLeft: "-2px",
                                          marginTop: "-1px",
                                          color: theme.palette.warning.main
                                        }}
                                    />
                                )}
                                          <i
                                              className="bhi-person"
                                              style={{fontSize: 12, color: theme.palette.text.main, opacity: 0.7}}
                                          />
                                          {user.get("fullName")}
                              </span>
                                      }
                                      onDelete={() => handleRemoveUser(userId)}
                                      deleteIcon={<i className="bhi-x" />}
                                      size="small"
                                  />
                                </Tooltip>
                            ) : null;
                          })}
                          {scheduleConfig.get("internalRecipientGroupIds")?.map((groupId) => {
                            const group = Groups.getGroup(groupId);
                            const hasAccess = accessLookup.groupAccessMap[groupId];

                            return group ? (
                                <Tooltip
                                    key={`group-${groupId}`}
                                    width={400}
                                    wrap={true}
                                    pointerStyle={{top: 20}}
                                    text={
                                      hasAccess ? (
                                          ""
                                      ) : (
                                          <>
                                <span style={{fontSize: "10px", paddingLeft: 12, marginBottom: 0}}>
                                  No Report Source Access
                                </span>
                                            <p
                                                style={{
                                                  marginTop: 0,
                                                  fontSize: "13px",
                                                  padding: "6px 12px 6px 12px",
                                                  marginBottom: 0
                                                }}
                                            >
                                              One or more users in this group don't have access to this report and
                                              will receive a
                                              PDF attachment only. Manage their permissions to grant full access to the
                                              report
                                              source.
                                            </p>
                                          </>
                                      )
                                    }
                                    disable={!!hasAccess}
                                    position="right"
                                >
                                  <Chip
                                      style={chipStyle}
                                      sx={chipSx}
                                      label={
                                        <span style={{display: "flex", alignItems: "center", gap: 4}}>
                                {!hasAccess && (
                                    <i
                                        className="bhi-caution"
                                        style={{fontSize: 12, color: theme.palette.warning.main}}
                                    />
                                )}
                                          <i
                                              className="bhi-users"
                                              style={{fontSize: 12, color: theme.palette.text.main, opacity: 0.7}}
                                          />
                                          {group.get("name")}
                              </span>
                                      }
                                      onDelete={() => handleRemoveGroup(groupId)}
                                      deleteIcon={<i className="bhi-x" />}
                                      size="small"
                                  />
                                </Tooltip>
                            ) : null;
                          })}
                        </div>
                    )}
                  </FormField>
                  <div style={{marginTop: "16px"}}>
                    <FormField label={"External Recipients"}>
                      <div style={{display: "flex", gap: "8px", alignItems: "flex-start"}}>
                        <div style={{flex: 1}}>
                          <DelayedTextField
                              size="small"
                              placeholder="Enter email address"
                              value={emailInput}
                              customStyle={{
                                height: 42,
                                borderColor: emailError ? theme.palette.error.main : undefined
                              }}
                              onChange={(value) => {
                                setEmailInput(value);
                                if (emailError) {
                                  setEmailError("");
                                }
                              }}
                              onKeyPress={handleEmailKeyPress}
                          />
                          {emailError && (
                              <span style={{
                                color: theme.palette.error.main,
                                fontSize: 11,
                                display: "block",
                                marginTop: 4
                              }}>
                                {emailError}
                              </span>
                          )}
                        </div>
                        <TextButton
                            label="Add"
                            type="primary"
                            disabled={!emailInput.trim()}
                            onClick={handleAddEmail}
                            style={{height: 42, minWidth: 60}}
                        />
                      </div>
                    </FormField>
                  </div>

                  {!scheduleConfig.get("externalRecipients").isEmpty() && (
                      <div style={chipContainerStyle}>
                        {scheduleConfig.get("externalRecipients").map((email) => (
                            <Tooltip
                                key={email}
                                width={400}
                                wrap={true}
                                pointerStyle={{top: 20}}
                                text={
                                  <>
                              <span
                                  style={{
                                    fontSize: "10px",
                                    paddingLeft: 12,
                                    marginBottom: 0
                                  }}>External Recipient</span>
                                    <p
                                        style={{
                                          marginTop: 0,
                                          fontSize: "13px",
                                          padding: "6px 12px 6px 12px",
                                          marginBottom: 0
                                        }}
                                    >
                                      This recipient is outside of your organisation. Be cautious about sharing
                                      sensitive
                                      information.
                                    </p>
                                  </>
                                }
                                position="right"
                            >
                              <Chip
                                  style={chipStyle}
                                  sx={chipSx}
                                  label={
                                    <span style={{display: "flex", alignItems: "center", gap: 4}}>
                          <span style={{position: "relative"}}>
                            <i
                                className="bhi-company"
                                style={{
                                  marginRight: 8,
                                  marginLeft: 3,
                                  fontSize: 14,
                                  opacity: 0.5,
                                  color: theme.palette.text.main
                                }}
                            />
                            <span css={strikethroughStyle(theme)}></span>
                          </span>
                                      {email}
                        </span>
                                  }
                                  onDelete={() => handleRemoveExternalEmail(email)}
                                  deleteIcon={<i className="bhi-x" />}
                                  size="small"
                              />
                            </Tooltip>
                        ))}
                      </div>
                  )}
                </div>
                <div style={{width: "49%"}}>
                  <div style={{marginBottom: "1rem"}}>
                    <FormField
                        label={"Message"}
                        tooltip={
                          "If you leave this blank, it will use the placeholder text shown."
                        } />
                  </div>
                  <FormField label={"Email Subject"}>
                    <DelayedTextField
                        data-test-id="scheduled-report-email-subject-input"
                        size="small"
                        maxLength={500}
                        value={scheduleConfig.get("emailSubject") || ""}
                        placeholder={emailPlaceholders.get("emailSubject").toString()}
                        customStyle={{
                          height: 42,
                          color: scheduleConfig.get("emailSubject")
                              ? theme.palette.text.main
                              : theme.palette.text.secondary
                        }}
                        onChange={(value) => setScheduleConfig((prev) => prev.set(
                            "emailSubject",
                            value ? value : null))}
                    />
                  </FormField>
                  <div style={{width: "100%", marginTop: "1rem"}}>
                    <FormField label={"Email Body"}>
                      <TextField
                          inputProps={{maxLength: 65000}}
                          sx={{
                            "& .MuiInputBase-root": {
                              background: theme.palette.background.card,
                              fontSize: "13px",
                              color: scheduleConfig.get("emailBody")
                                  ? theme.palette.text.main
                                  : theme.palette.text.secondary
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "13px",
                              color: scheduleConfig.get("emailBody")
                                  ? theme.palette.text.main
                                  : theme.palette.text.secondary
                            },
                            "& .MuiInputBase-input::placeholder": {
                              opacity: scheduleConfig.get("emailBody") ? 0 : 1
                            }
                          }}
                          multiline
                          rows={8}
                          fullWidth
                          size="small"
                          placeholder={emailPlaceholders.get("emailBody").toString()}
                          value={scheduleConfig.get("emailBody") || ""}
                          onChange={(e) => setScheduleConfig((prev) => prev.set(
                              "emailBody",
                              e.target.value ? e.target.value : null))}
                      />
                      <TextButton
                          icon="email"
                          iconType="bhi"
                          key="send-preview"
                          label="Send Me a Preview"
                          type="inverted"
                          onClick={() => {
                            setPreviewError(null);
                            sentPreviewIdsRef.current = Immutable.Set();
                            previewSentAtRef.current = null;
                            setIsWaitingForPreview(true);
                            handleSendPreview();
                          }}
                          style={{marginTop: "8px"}}
                          disabled={!canSendPreview}
                      />
                      {!pendingPreviews.isEmpty() && (
                          <span
                              style={{
                                display: "block",
                                fontSize: 11
                              }}>Sending {Pluralize("report", pendingPreviews.count(), true)}...</span>
                      )}
                      {previewError && (
                          <span
                              style={{
                                display: "block",
                                fontSize: 11,
                                color: theme.palette.error.main
                              }}>{previewError}</span>
                      )}
                      <span style={{display: "block", fontSize: 11}}>
                    Preview will be delivered to:{" "}
                        <span style={{textDecoration: "underline"}}>{Users.getCurrentUser().get("email")}</span>
                  </span>
                    </FormField>
                  </div>
                </div>
              </div>
            </div>
            <div css={drawerControlsStyle(theme, DRAWER_WIDTH)}>
              <div style={{display: "flex", alignItems: "center"}}>
                <Tooltip
                    disable={!hasUnsavedChanges && !cloneFromId && !isClonedFromEdit}
                    text={cloneFromId || isClonedFromEdit
                        ? "Save this schedule before cloning again"
                        : "Save changes before cloning"}
                    position="top"
                >
                  <TextButton
                      testId="clone-schedule-button"
                      label="Clone"
                      icon="duplicate"
                      iconType="bhi"
                      onClick={handleCloneButtonClick}
                      disabled={hasUnsavedChanges || !!cloneFromId || isClonedFromEdit}
                      style={{
                        marginRight: "1rem",
                        backgroundColor: theme.palette.background.card,
                        border: `1px solid ${theme.palette.primary.main}`,
                        color: theme.palette.primary.main
                      }}
                  />
                </Tooltip>
                {userCanDelete && !isNewReport && !isClonedFromEdit && !cloneFromId && <TextButton
                    testId="delete-schedule-button"
                    label="Delete"
                    icon="delete-o"
                    iconType="bhi"
                    onClick={handleDeleteButtonClick}
                    style={{
                      marginRight: "1rem",
                      backgroundColor: theme.palette.background.card,
                      border: `1px solid ${theme.palette.button.alert}`,
                      color: theme.palette.button.alert
                    }}
                />}
              </div>
              <div style={{display: "flex", flexGrow: 1}} />
              <TextButton
                  testId="discard-all-changes-button"
                  label="Cancel"
                  onClick={handleCloseRequest}
                  type={"inverted"}
                  css={discardButtonStyle()}
              />
              <TextButton
                  testId="save-schedule-button"
                  type="primary"
                  label="Save Schedule"
                  onClick={handleSaveSchedule}
                  disabled={!isValidSchedule}
                  css={applyButtonStyle}
              />
            </div>

            {showAccessDialog && !isCheckingAccess && (
                <Dialog
                    width={680}
                    minHeight={500}
                    label="Some users can't access this report in Analytics"
                    titleStyle={{fontSize: 20}}
                    onRequestClose={() => setShowAccessDialog(false)}
                    content={
                      <ScheduleAccessDialog
                          usersWithoutAccess={accessData.get("usersWithoutAccess")}
                          groupsWithoutAccess={accessData.get("groupsWithoutAccess")}
                          usersWithAccess={accessData.get("usersWithAccess")}
                          groupsWithAccess={accessData.get("groupsWithAccess")}
                          externalRecipients={scheduleConfig.get("externalRecipients")}
                          onSaveSchedule={saveSchedule}
                          onCancel={() => setShowAccessDialog(false)}
                      />
                    }
                />
            )}

            {showUnsavedDialog && (
                <Dialog
                    width={560}
                    minHeight={230}
                    label="Unsaved Changes"
                    onRequestClose={() => setShowUnsavedDialog(false)}
                    content={
                      <ScheduleUnsavedChangesDialog
                          onDiscard={handleDiscardChanges}
                          onCancel={() => setShowUnsavedDialog(false)}
                      />
                    }
                />
            )}

            {isDeleteDialogOpen && (
                <ConfirmDialog>
                  <div style={{width: "600px", textAlign: "center"}}>
                    <svg width="58" height="58" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" fill="none" />
                      <line x1="12" y1="8" x2="12" y2="12" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="12" cy="16" r="1" fill="#F59E0B" />
                    </svg>
                    <p style={{fontSize: "1.6rem", fontWeight: "500", marginBottom: 0}}>
                      Are you sure you want to delete this report permanently?
                    </p>
                    <p style={{fontSize: "1.2rem"}}>
                      Once you delete you will not be able to undo this action.
                    </p>
                    <hr />

                    <div style={{display: "flex", gap: "10px", marginTop: "20px", justifyContent: "end"}}>
                      <TextButton
                          label="Cancel"
                          onClick={() => setIsDeleteDialogOpen(false)} />
                      <TextButton
                          testId="confirm-delete-report-button"
                          icon="trash"
                          type="alert"
                          label="Delete"
                          onClick={handleConfirmDelete} />
                    </div>
                  </div>
                </ConfirmDialog>
            )}
          </>
      );
    }
);

export default ScheduleFormContent;
